import { getArticleById } from './api/getArticleById';
import { HttpClientFactory } from './httpClient';
import type { ArticleWithComponents } from './reducers/types';

const MAX_CACHED_ARTICLES = 30;

const cache = new Map<string, ArticleWithComponents>();
const inFlight = new Map<string, Promise<ArticleWithComponents>>();

const setCachedArticle = (id: string, article: ArticleWithComponents): void => {
  cache.delete(id);
  cache.set(id, article);
  if (cache.size > MAX_CACHED_ARTICLES) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) {
      cache.delete(oldest);
    }
  }
};

const fetchAndCache = (id: string, httpClient: HttpClientFactory) => {
  const request = getArticleById(id, httpClient)
    .then((article) => {
      setCachedArticle(id, article);
      return article;
    })
    .finally(() => {
      inFlight.delete(id);
    });
  inFlight.set(id, request);
  return request;
};

export const prefetchArticleById = (id: string, httpClient: HttpClientFactory): void => {
  if (!id || cache.has(id) || inFlight.has(id)) {
    return;
  }
  fetchAndCache(id, httpClient).catch(() => undefined);
};

export const resolveArticleById = (
  id: string,
  httpClient: HttpClientFactory,
): Promise<ArticleWithComponents> => {
  const cached = cache.get(id);
  if (cached) {
    return Promise.resolve(cached);
  }
  return inFlight.get(id) ?? fetchAndCache(id, httpClient);
};
